import { Injectable } from '@angular/core';
import { MarketplaceAppService } from '@vendasta/marketplace-packages';
import {
  ListSubstitutionParametersRequestFiltersInterface,
  SubstitutionParameter,
  TemplatesService,
} from '@vendasta/templates';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { BUSINESS_CUSTOM_DATA, SALESPERSON_CUSTOM_DATA } from './variable-menu-dialog/variable-menu-dialog.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SchemaService } from '@vendasta/auxiliary-data-components';
import { SubstitutionParameterOptionsInterface } from '@vendasta/templates';

export interface Option {
  title: string;
  value: string;
}

export interface VariablesFilters {
  includeHtml?: boolean;
  dontIncludeDefaultCategories?: boolean;
  includeDeprecated?: boolean;
  // forRequiredOptions should be used for filtering out variables you don't have tue required data to render with but
  // it currently is not used, unfortunately
  forRequiredOptions?: SubstitutionParameterOptionsInterface;
}

interface VariableMenuItemSettings {
  requiredParams: string[];
  // By default the replacement tag is surrounded by '{{' and '}}' so that templates µs can interpolate them. This
  // flag turns that off
  shouldNotSurroundWithCurlyBraces?: boolean;
}

export interface VariableMenuCategory {
  textKey: string;
  subItems$: Observable<VariableMenuItem[]>;
  settings?: VariableMenuItemSettings;
}

export interface VariableMenuItem {
  textKey: string;
  // VariableMenuItems either have a replacement tag (if they are a leaf) or subItems if they are just part of a branch
  replacementTag: string;
  subItems$?: Observable<VariableMenuItem[]>;
  settings?: VariableMenuItemSettings; //Not sure if settings is requried on the VariableMenuItem or just the category
}

@Injectable({
  providedIn: 'root',
})
export class VariableMenuService {
  allSubstitutionParameters$: Observable<SubstitutionParameter[]> = this.templatesService
    .listSubstitutionParameters({ includeHtml: true })
    .pipe(shareReplay(1));

  private buildVariables(
    partnerId: string,
    marketId: string,
    filters: ListSubstitutionParametersRequestFiltersInterface,
  ): VariableMenuCategory[] {
    return [
      {
        textKey: 'HTML_EDITOR.DYNAMIC_CONTENT.ACCOUNT_DETAILS',
        subItems$: this.filterAllSubstitionParametersToSupportedReplacementTags(
          [
            'businessName',
            'businessWebsite',
            'customerIdentifier',
            'accountIdentifier',
            'companyIdentifier',
            'businessPhoneNumber',
            'businessCountry',
            'businessState',
            'businessStreetAddress',
            'businessStreetAddress2',
            'businessCity',
            'businessPostalCode',
            'businessOrigin',
            'businessCategoryIds',
            'businessEmail',
          ],
          filters,
        ),
      },
      {
        textKey: 'HTML_EDITOR.DYNAMIC_CONTENT.PARTNER_DETAILS',
        subItems$: this.filterAllSubstitionParametersToSupportedReplacementTags(
          [
            'partnerName',
            'partnerWebsite',
            'partnerEmail',
            'partnerPhone',
            'partnerStreetAddress',
            'partnerStreetAddress2',
            'partnerCity',
            'partnerState',
            'partnerCountry',
            'partnerPostalCode',
          ],
          filters,
        ),
      },
      {
        textKey: 'HTML_EDITOR.DYNAMIC_CONTENT.ORDER_DETAILS',
        subItems$: this.filterAllSubstitionParametersToSupportedReplacementTags(
          [
            'orderId',
            'orderLineItems',
            'status',
            'orderBusinessName',
            'expiryDate',
            'contractStartDate',
            'userContact',
          ],
          filters,
        ),
        settings: {
          requiredParams: ['isOrderAutomation'],
        },
      },
      {
        textKey: 'HTML_EDITOR.DYNAMIC_CONTENT.ORDER_SALESPERSON_DETAILS.TITLE',
        subItems$: this.filterAllSubstitionParametersToSupportedReplacementTags(
          [
            'salesPersonInformation',
            'orderSalesPersonFirstName',
            'orderSalesPersonLastName',
            'orderSalesPersonPhoneNumber',
            'orderSalesPersonEmail',
            'orderSalesPersonJobTitle',
            'orderSalesPersonPhoto',
            'salesPersonId',
          ],
          filters,
        ),
        settings: {
          requiredParams: ['isOrderAutomation'],
        },
      },
      {
        textKey: 'HTML_EDITOR.DYNAMIC_CONTENT.SALESPERSON_DETAILS',
        subItems$: this.filterAllSubstitionParametersToSupportedReplacementTags(
          [
            'salesPersonContactCard',
            'salesPersonFirstName',
            'salesPersonLastName',
            'salesPersonEmail',
            'salesPersonPhoneNumber',
            'salesPersonJobTitle',
            'salesPersonLoginUrl',
          ],
          filters,
        ),
      },
      {
        textKey: 'HTML_EDITOR.DYNAMIC_CONTENT.BUSINESS_APP_DETAILS',
        subItems$: this.filterAllSubstitionParametersToSupportedReplacementTags(
          ['businessAppName', 'businessAppUrl', 'businessAppStoreUrl', 'businessAppPreviewUrl'],
          filters,
        ),
      },
      {
        textKey: 'HTML_EDITOR.DYNAMIC_CONTENT.PRODUCT_DETAILS.TITLE',
        subItems$: this.filterAllSubstitionParametersToSupportedReplacementTags(['productName'], filters).pipe(
          map((subItems) => {
            if ((subItems || []).length > 0) {
              subItems[0].subItems$ = this.marketplacePackagesService.getAllActivatableApps(partnerId, marketId).pipe(
                shareReplay(1),
                map((apps) => {
                  return apps.map((app) => ({
                    replacementTag: "productName['" + app.appId + "']",
                    textKey: app.name,
                  }));
                }),
              );
            }
            return subItems;
          }),
        ),
        settings: {
          requiredParams: ['partnerId', 'marketId'],
        },
      },
      {
        textKey: 'HTML_EDITOR.DYNAMIC_CONTENT.CUSTOM_DATA_DETAILS.TITLE',
        subItems$: this.filterAllSubstitionParametersToSupportedReplacementTags(
          [SALESPERSON_CUSTOM_DATA, BUSINESS_CUSTOM_DATA],
          filters,
        ).pipe(
          map((menuItems) => {
            if ((menuItems || []).length > 0) {
              menuItems.map((menuItem) => {
                if (menuItem.replacementTag === SALESPERSON_CUSTOM_DATA) {
                  menuItem.subItems$ = this.schemaService.listFieldSchemas('user', partnerId, false, '', 100).pipe(
                    map((response) => response.fieldSchemas),
                    map((fieldSchemas) =>
                      fieldSchemas.map((schema) => ({
                        textKey: schema.fieldName,
                        replacementTag: SALESPERSON_CUSTOM_DATA + "['" + schema.fieldId + "']",
                      })),
                    ),
                  );
                }
                if (menuItem.replacementTag === BUSINESS_CUSTOM_DATA) {
                  menuItem.subItems$ = this.schemaService.listFieldSchemas('business', partnerId, false, '', 100).pipe(
                    map((response) => response.fieldSchemas),
                    map((fieldSchemas) =>
                      fieldSchemas.map((schema) => ({
                        textKey: schema.fieldName,
                        replacementTag: BUSINESS_CUSTOM_DATA + "['" + schema.fieldId + "']",
                      })),
                    ),
                  );
                }
              });
            }
            return menuItems;
          }),
        ),
        settings: {
          requiredParams: ['partnerId'],
        },
      },
    ];
  }

  constructor(
    private readonly templatesService: TemplatesService,
    private readonly marketplacePackagesService: MarketplaceAppService,
    private readonly schemaService: SchemaService,
  ) {}

  public getVariables(
    partnerId: string,
    marketId: string,
    filters: VariablesFilters,
    extraMenuItems: VariableMenuItem[] = [],
  ): VariableMenuItem[] {
    const variables = this.buildVariables(partnerId, marketId, filters);
    let variablesFromCategories: VariableMenuItem[] = variables.map((v) => ({
      textKey: v.textKey,
      settings: v.settings,
      replacementTag: '', // This is the top level category so it doesn't have a replacement tag
      subItems$: v.subItems$,
    }));
    if (filters.dontIncludeDefaultCategories) {
      variablesFromCategories = [];
    }
    return variablesFromCategories.concat(extraMenuItems);
  }

  private variableMenuItemFromSubstitutionParameter(s: SubstitutionParameter): VariableMenuItem {
    return {
      replacementTag: s.replacementTag,
      textKey: s.displayName,
    };
  }

  private filterAllSubstitionParametersToSupportedReplacementTags(
    supportedReplacementTags: string[],
    filters: ListSubstitutionParametersRequestFiltersInterface,
  ): Observable<VariableMenuItem[]> {
    return this.allSubstitutionParameters$.pipe(
      map((params) => {
        const filteredParams = params.filter((p) => {
          return !(!filters.includeHtml && p.isHtml);
        });
        return filteredParams
          .filter((p) => supportedReplacementTags.some((t) => t === p.replacementTag))
          .sort(function (a, b): number {
            return (
              supportedReplacementTags.indexOf(a.replacementTag) - supportedReplacementTags.indexOf(b.replacementTag)
            );
          })
          .map(this.variableMenuItemFromSubstitutionParameter);
      }),
    );
  }
}
